import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import api from "../../lib/api";
import { toast } from "sonner";
import { Bell, BellOff, CheckCheck, ChevronRight, AlertOctagon, AlertTriangle, Info, Receipt } from "lucide-react";

const SEV_ICON = { critical: AlertOctagon, warn: AlertTriangle, info: Info };
const SEV_CLS = {
  critical: "bg-rose-50 text-rose-600 border-rose-200",
  warn:     "bg-amber-50 text-amber-600 border-amber-200",
  info:     "bg-sky-50 text-sky-600 border-sky-200",
};

export default function NotificationsBell() {
  const [data, setData] = useState({ notifications: [], unread_count: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    api.get("/notifications?limit=20")
      .then((r) => setData(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      load();
    } catch (e) { toast.error("Failed to mark as read"); }
  };
  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      load();
      toast.success("Marked all as read");
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative inline-flex items-center justify-center w-9 h-9 rounded-xl text-white hover:bg-white/10 transition-colors"
          data-testid="customer-notif-bell"
          aria-label={`${data.unread_count} unread notifications`}
        >
          <Bell className="w-4 h-4" />
          {data.unread_count > 0 && (
            <Badge
              data-testid="customer-notif-count"
              className="absolute -top-1 -right-1 h-5 min-w-[20px] rounded-full p-0 px-1 bg-rose-500 hover:bg-rose-500 text-white text-[10px] font-bold tabular-nums flex items-center justify-center border-2 border-[#0a0e27]"
            >
              {data.unread_count > 99 ? "99+" : data.unread_count}
            </Badge>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 rounded-2xl overflow-hidden" data-testid="customer-notif-panel">
        <div className="flex items-center justify-between px-4 py-3 bg-muted/30 border-b border-border">
          <div className="font-display font-bold text-foreground">Notifications</div>
          {data.unread_count > 0 && (
            <Button onClick={markAllRead} size="sm" variant="ghost" className="text-xs gap-1 h-7" data-testid="customer-notif-readall-btn">
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </Button>
          )}
        </div>
        {data.notifications.length === 0 ? (
          <div className="p-10 text-center" data-testid="customer-notif-empty">
            <BellOff className="w-7 h-7 text-muted-foreground mx-auto mb-2" />
            <div className="text-sm text-muted-foreground">All caught up.</div>
          </div>
        ) : (
          <div className="max-h-[420px] overflow-y-auto divide-y divide-border" data-testid="customer-notif-list">
            {data.notifications.map((n) => {
              const Icon = SEV_ICON[n.severity] || Info;
              const cls = SEV_CLS[n.severity] || SEV_CLS.info;
              const onClick = () => {
                if (!n.read) markRead(n.id);
                if (n.order_id) { setOpen(false); navigate(`/account/orders?o=${n.order_id}`); }
              };
              return (
                <button
                  key={n.id}
                  onClick={onClick}
                  className={`w-full text-left px-4 py-3 hover:bg-muted/30 flex items-start gap-3 transition-colors ${!n.read ? "bg-primary/[0.03]" : ""}`}
                  data-testid={`notif-${n.id}`}
                >
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 border ${cls}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <div className="font-semibold text-foreground text-sm truncate">{n.title}</div>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-primary shrink-0" />}
                    </div>
                    {n.body && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</div>}
                    <div className="text-[10px] text-muted-foreground tabular-nums mt-1">
                      {new Date(n.ts).toLocaleString("en-IE")}
                    </div>
                  </div>
                  {n.order_id && <ChevronRight className="w-4 h-4 text-muted-foreground/40 mt-1 shrink-0" />}
                </button>
              );
            })}
          </div>
        )}
        <div className="px-4 py-2.5 bg-muted/20 border-t border-border text-[10px] text-muted-foreground flex items-center gap-1.5">
          <Receipt className="w-3 h-3" /> Email delivery rolling out soon — turn on in Settings when it's live.
        </div>
      </PopoverContent>
    </Popover>
  );
}
